import type { ScheduleCoverageRow } from "./schedule";
import { slotTone, type SlotTone } from "./schedule-tone";

// Schedule Builder week-grid model (CONSOLE-SCHEDULE-BUILDER-SCAFFOLD). Pure derivation over
// the EXISTING schedule_coverage read rows - zero mutations, zero new queries beyond a week
// window. Posts (site - route) down, Mon..Sun across on the ISO service week, exactly the
// approved standalone's grid.
// A slot lands in the column of its slot_date; midnight-crossers stay in their START date's
// column and carry the +1 marker. Tone vocabulary is imported from the accepted coverage
// surface so the builder and the coverage report can never drift apart.

export interface BuilderSlot {
  id: string;
  /** e.g. "22:00 -> 06:00 +1" rendered in the display timezone. */
  window: string;
  fraction: string;
  tone: SlotTone;
  credentials: readonly string[];
  /** True only for published slots with open headcount - mirrors the RPC's own gate. */
  assignable: boolean;
  /** True for published slots - the cancel RPC's own gate; server still refuses worked slots. */
  cancellable: boolean;
  /** Published slot with at least one assigned seat (filled or partial tone). */
  replaceable: boolean;
  /** Client heuristic: published slot whose scheduled start is still in the future. */
  reviseBoundsEligible: boolean;
  startsAt: string | null;
  endsAt: string | null;
  headcountRequired: number;
}

export interface BuilderDay {
  date: string;
  slots: BuilderSlot[];
}

export interface BuilderPost {
  siteName: string;
  routeName: string;
  days: BuilderDay[];
}

export interface WeekGrid {
  weekStart: string;
  days: string[];
  posts: BuilderPost[];
  summary: { totalSlots: number; openSlots: number; atRiskSlots: number };
}

// --- Pure date math on YYYY-MM-DD strings (UTC-anchored; no local-tz surprises) ------------

function toUtcDate(dateIso: string): Date {
  const [year, month, day] = dateIso.split("-").map(Number);
  return new Date(Date.UTC(year!, month! - 1, day!));
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Snap any YYYY-MM-DD to its week's Monday.
 *
 * The service week is the ISO Monday week. Approval, the frozen service facts, and the host
 * publish gate are all keyed on it, so a Sunday-anchored grid would show a confident week of
 * seven days that no service week owns. Monday is the anchor everywhere: grid, wizard, URL,
 * and the window handed to the host.
 */
export function weekStartIso(dateIso: string): string {
  const date = toUtcDate(dateIso);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
  return toIsoDate(date);
}

/** Seven consecutive dates beginning at the given week start. */
export function weekDays(startIso: string): string[] {
  const start = toUtcDate(startIso);
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + index);
    return toIsoDate(day);
  });
}

/** Move a week start by whole weeks (negative = back). */
export function shiftWeek(startIso: string, deltaWeeks: number): string {
  const start = toUtcDate(startIso);
  start.setUTCDate(start.getUTCDate() + deltaWeeks * 7);
  return toIsoDate(start);
}

// --- Slot rendering helpers -------------------------------------------------------------

function clock(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone }).format(
    new Date(iso),
  );
}

function dayInZone(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).format(
    new Date(iso),
  );
}

function windowLabel(row: ScheduleCoverageRow, timeZone: string): string {
  if (row.startsAt === null || row.endsAt === null) {
    return "time not set";
  }
  const crosses = dayInZone(row.startsAt, timeZone) !== dayInZone(row.endsAt, timeZone);
  return `${clock(row.startsAt, timeZone)} -> ${clock(row.endsAt, timeZone)}${crosses ? " +1" : ""}`;
}

// --- Grid -------------------------------------------------------------------------------

export function buildWeekGrid(
  rows: readonly ScheduleCoverageRow[],
  weekStart: string,
  timeZone: string = "America/New_York",
): WeekGrid {
  const days = weekDays(weekStart);
  const dayIndex = new Map(days.map((date, index) => [date, index]));
  const posts = new Map<string, BuilderPost>();
  let totalSlots = 0;
  let openSlots = 0;
  let atRiskSlots = 0;

  for (const row of rows) {
    if (row.slotDate === null || !dayIndex.has(row.slotDate)) {
      continue; // outside this week - dropped, never misplaced
    }
    const key = `${row.siteName ?? ""}::${row.routeName ?? ""}`;
    let post = posts.get(key);
    if (post === undefined) {
      post = {
        siteName: row.siteName ?? "Site",
        routeName: row.routeName ?? "Route",
        days: days.map((date) => ({ date, slots: [] })),
      };
      posts.set(key, post);
    }
    const tone = slotTone(row);
    const startsAt = row.startsAt;
    const beforeStart =
      row.slotStatus === "published" &&
      startsAt !== null &&
      !Number.isNaN(new Date(startsAt).getTime()) &&
      new Date(startsAt) > new Date();
    post.days[dayIndex.get(row.slotDate)!]!.slots.push({
      id: row.shiftSlotId,
      window: windowLabel(row, timeZone),
      fraction: `${row.assignedCount} of ${row.headcountRequired}`,
      tone,
      credentials: row.requiredCredentialTypes,
      assignable: row.slotStatus === "published" && row.assignedCount < row.headcountRequired,
      cancellable: row.slotStatus === "published",
      replaceable:
        row.slotStatus === "published" && (tone === "filled" || tone === "partial"),
      reviseBoundsEligible: beforeStart,
      startsAt,
      endsAt: row.endsAt,
      headcountRequired: row.headcountRequired,
    });
    totalSlots += 1;
    if (tone === "open") openSlots += 1;
    if (tone === "at-risk") atRiskSlots += 1;
  }

  return {
    weekStart,
    days,
    posts: [...posts.values()].sort((a, b) =>
      `${a.siteName}${a.routeName}`.localeCompare(`${b.siteName}${b.routeName}`),
    ),
    summary: { totalSlots, openSlots, atRiskSlots },
  };
}
